import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, FILE_BASE_URL } from '../../../apiConfig';
import { motion } from 'framer-motion';
import { Trophy, Star, Users, Map, Target } from 'lucide-react';
import useBreakpoint from '../../../hooks/useBreakpoint';

const PREMIUM_GRAD = 'linear-gradient(180deg, #0A1172 0%, #040960 100%)';
const ACCENT = '#E7C424';

const Summary = () => {
    const { isMobile } = useBreakpoint();
    const [stats, setStats] = useState({
        divisions: '0',
        teams: '0',
        hundreds: '0',
        sixes: '0',
        heading: 'SEASON STATISTICS',
        batting_leaders: [], 
        player_leaders: [],
        fielding_leaders: [],
        bowling_leaders: []
    });
    const [loading, setLoading] = useState(true);
    const [activeBatting, setActiveBatting] = useState(0);
    const [activePlayer, setActivePlayer] = useState(0);
    const [activeFielding, setActiveFielding] = useState(0);
    const [activeBowling, setActiveBowling] = useState(0);

    useEffect(() => {
        let isMounted = true;
        axios.get(`${API_BASE_URL}/seasonStatistics/1`)
            .then((res) => {
                if (isMounted && res.data && res.data.stats) {
                    const s = res.data.stats;
                    
                    setStats({
                        divisions: s.divisions_count || '0',
                        teams: s.teams_count || '0',
                        hundreds: s.hundreds_count || '0',
                        sixes: s.sixes_count || '0',
                        heading: s.heading || 'SEASON STATISTICS',
                        batting_leaders: s.batting_leaders || [],
                        player_leaders: s.player_leaders || [],
                        fielding_leaders: s.fielding_leaders || [],
                        bowling_leaders: s.bowling_leaders || []
                    });
                }
            })
            .catch(err => console.error("Summary fetch error:", err))
            .finally(() => setLoading(false));

        return () => { isMounted = false; };
    }, []);

    // Carousel Logic
    useEffect(() => {
        if (loading) return;
        
        const interval = setInterval(() => {
            if (stats.batting_leaders.length > 1) setActiveBatting(prev => (prev + 1) % stats.batting_leaders.length);
            if (stats.player_leaders.length > 1) setActivePlayer(prev => (prev + 1) % stats.player_leaders.length);
            if (stats.fielding_leaders.length > 1) setActiveFielding(prev => (prev + 1) % stats.fielding_leaders.length);
            if (stats.bowling_leaders.length > 1) setActiveBowling(prev => (prev + 1) % stats.bowling_leaders.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [loading, stats.batting_leaders.length, stats.player_leaders.length, stats.fielding_leaders.length, stats.bowling_leaders.length]);

    if (loading) return null;

    const StatCard = ({ label, value, color = '#E7C424' }) => (
        <motion.div 
            whileHover={{ y: -10, scale: 1.02 }}
            style={{
                background: color,
                padding: isMobile ? '20px 10px' : '40px 20px',
                borderRadius: '0px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
                flex: 1,
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
            }}
        >
            <h3 style={{ fontFamily: 'var(--fontDisplay)', fontSize: isMobile ? '32px' : '56px', fontWeight: 900, color: '#fff', margin: 0, lineHeight: 1 }}>{value}</h3>
            <p style={{ fontFamily: 'var(--fontDisplay)', fontSize: isMobile ? '12px' : '16px', fontWeight: 800, color: '#fff', margin: 0, textTransform: 'uppercase', opacity: 0.9, letterSpacing: '1px' }}>{label}</p>
        </motion.div>
    );

    const SliderCard = ({ title, items, activeIndex, type = 'player' }) => {
        const item = items[activeIndex] || {};
        const isBatting = title.includes('BATTING');
        const isBowling = title.includes('BOWLING');
        const isFielding = title.includes('FIELDING');
        
        // Data Resolution
        const photo = item.photo || item.logo;
        const teamLogo = isBatting || isBowling ? item.logo : null; // Usually we don't have separate team logo in current data, but we'll try
        
        // Parsing "Against" team from match name (e.g., "Team A vs Team B")
        const getAgainst = (matchStr) => {
            if (!matchStr) return '';
            const parts = matchStr.split(/\s+vs\s+/i);
            if (parts.length > 1) {
                // If it's a "Player" record, the "Against" is usually the second part
                return parts[1].trim();
            }
            return matchStr;
        };

        // Badge Logic
        const getBadge = () => {
            if (isBatting) {
                const score = parseInt(item.score);
                if (score >= 100) return 'Century';
                if (score >= 50) return 'Half-Century';
            } else if (isBowling) {
                const wkts = parseInt(item.wickets);
                if (wkts >= 5) return '5 Wickets';
                if (wkts >= 3) return 'Solid Spell';
            } else if (isFielding) {
                const victims = parseInt(item.total_victims);
                if (victims >= 3) return 'Flash';
            }
            return null;
        };

        const badge = getBadge();

        return (
            <motion.div 
                key={`${title}-${activeIndex}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
                style={{
                    background: 'linear-gradient(135deg, #0A1172 0%, #040960 100%)',
                    padding: '20px',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    minHeight: '260px',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.3)',
                    overflow: 'hidden',
                    color: '#fff'
                }}
            >
                {/* CATEGORY HEADING */}
                <div style={{ marginBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Trophy size={14} color="#FFD700" />
                    <span style={{ fontSize: '11px', fontWeight: 900, color: '#FFD700', letterSpacing: '2px', textTransform: 'uppercase' }}>{title}</span>
                </div>

                {/* TOP ROW: DATE & TEAM */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', zIndex: 2 }}>
                    <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>{item.date || '01 June 2025'}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 800, color: '#fff', opacity: 0.9, maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.club_name || 'BTCL CLUB'}
                        </span>
                    </div>
                </div>

                {/* MIDDLE ROW: STATS & ICON */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, position: 'relative', zIndex: 2, textAlign: 'center' }}>
                    
                    {/* Category Specific Large Icon */}
                    <div style={{ marginBottom: '20px', opacity: 0.8 }}>
                        {isBatting ? <Trophy size={48} color={ACCENT} /> : 
                         isBowling ? <Target size={48} color={ACCENT} /> : 
                         <Users size={48} color={ACCENT} />}
                    </div>

                    <h4 style={{ fontFamily: 'var(--fontDisplay)', fontSize: '24px', fontWeight: 900, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>{item.name || 'BTCL PLAYER'}</h4>
                    
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '15px', marginBottom: '15px' }}>
                        <span style={{ fontSize: '72px', fontWeight: 900, lineHeight: 1, color: '#fff' }}>
                            {isBatting ? item.score : isBowling ? (item.wickets || '0') : (item.total_victims || '0')}
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <span style={{ fontSize: '16px', color: ACCENT, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                {isBatting ? 'Runs' : isBowling ? 'Wkts' : 'Victims'}
                            </span>
                            {badge && (
                                <div style={{ background: '#fff', color: '#040960', padding: '4px 12px', borderRadius: '2px', fontSize: '11px', fontWeight: 900, marginTop: '5px', textTransform: 'uppercase' }}>
                                    {badge}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* BOTTOM ROW: OPPOSITION & NAV */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 2, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '15px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700 }}>
                        <span style={{ opacity: 0.6, fontWeight: 500 }}>Against</span> <span style={{ textTransform: 'uppercase', color: ACCENT }}>{getAgainst(item.match)}</span>
                    </div>
                    
                    {/* Navigation Dots */}
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '5px' }}>
                        {items.length > 1 && items.map((_, i) => (
                            <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', background: i === activeIndex ? '#FFD700' : 'rgba(255,255,255,0.3)', transition: '0.3s' }} />
                        ))}
                    </div>
                </div>
            </motion.div>
        );
    };

    return (
        <section style={{ maxWidth: '1280px', margin: '0 auto 80px auto', padding: '0 24px', display: 'flex', flexDirection: 'column', gap: '40px' }}>
            <div style={{ textAlign: isMobile ? 'left' : 'center' }}>
                <h2 style={{ fontFamily: 'var(--fontDisplay)', fontSize: isMobile ? '40px' : '64px', fontWeight: 900, color: '#000', margin: 0, textTransform: 'uppercase', fontStyle: 'italic', lineHeight: 0.9 }}>{stats.heading}</h2>
            </div>

            {/* BIG SEASON STATISTICS ROW */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
                gap: '15px' 
            }}>
                <StatCard label="Divisions" value={stats.divisions} color="#00C853" />
                <StatCard label="Teams" value={stats.teams} color="#FF6D00" />
                <StatCard label="Hundreds" value={stats.hundreds} color="#FFD600" />
                <StatCard label="Sixes" value={stats.sixes} color="#0D47A1" />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '20px' }}>
                <SliderCard title="TOP PERFORMANCE BATTING" items={stats.batting_leaders} activeIndex={activeBatting} type="team" />
                <SliderCard title="TOP PERFORMANCE BOWLING" items={stats.bowling_leaders} activeIndex={activeBowling} type="player" />
                <SliderCard title="TOP PERFORMANCE FIELDING" items={stats.fielding_leaders} activeIndex={activeFielding} type="player" />
            </div>
        </section>
    );
};

export default Summary;
